/**
 * What the analysis layers show, kept outside React's tree: a tiny store that the 2D overlay, the dock and the 3D
 * view subscribe to. The sun animation and the scrubber change it many times a second; only the components that
 * read the changed fields render (useSyncExternalStore with a selector), and the 3D view applies it to the scene
 * directly without any React render.
 */
import { useSyncExternalStore } from "react";
import type { Vec2 } from "../geo.ts";

/** The heatmap drawn over the roof / site (one at a time). */
export type RasterLayer = "none" | "height" | "irradiance" | "access" | "tsrf";
export type Cloud = "off" | "points" | "surface";

export interface AnalysisState {
  raster: RasterLayer;
  /** 0 = the year, 1-12 = a month (irradiance). */
  month: number;
  contours: boolean;
  panelKwh: boolean;
  /** Sun & shade: the sun path, the scrubber, shadows. */
  sun: boolean;
  /** Day of the year (1-365) and solar time (minutes) of the sun. */
  day: number;
  minutes: number;
  playing: boolean;
  /** The DSM in 3D: off, coloured points or a coloured surface. */
  cloud: Cloud;
  /** The cross-section line (local metres). */
  section: { a: Vec2; b: Vec2 } | null;
}

export const INITIAL: AnalysisState = {
  raster: "none",
  month: 0,
  contours: true,
  panelKwh: false,
  sun: false,
  day: 172, // 21 June
  minutes: 15 * 60,
  playing: false,
  cloud: "off",
  section: null,
};

export interface AnalysisStore {
  get: () => AnalysisState;
  set: (patch: Partial<AnalysisState> | ((s: AnalysisState) => Partial<AnalysisState>)) => void;
  subscribe: (listener: () => void) => () => void;
}

export function createAnalysisStore(initial: AnalysisState = INITIAL): AnalysisStore {
  let state = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    set: (patch) => {
      const next = typeof patch === "function" ? patch(state) : patch;
      let changed = false;
      for (const k of Object.keys(next) as (keyof AnalysisState)[]) if (next[k] !== state[k]) changed = true;
      if (!changed) return;
      state = { ...state, ...next };
      listeners.forEach((l) => l());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export function useAnalysisState<T>(store: AnalysisStore, select: (s: AnalysisState) => T): T {
  return useSyncExternalStore(store.subscribe, () => select(store.get()));
}

/** Whether anything needs the analysis data (it is fetched only then). */
export function needsData(s: AnalysisState, reportOpen: boolean): boolean {
  return s.raster !== "none" || s.panelKwh || s.sun || s.cloud !== "off" || s.section != null || reportOpen;
}

/** Steps the time of day; past sunset it wraps to the next day's morning. */
export function stepMinutes(s: AnalysisState, delta: number): Partial<AnalysisState> {
  let minutes = s.minutes + delta;
  let day = s.day;
  if (minutes >= 1440) {
    minutes -= 1440;
    day = (day % 365) + 1;
  } else if (minutes < 0) {
    minutes += 1440;
    day = day === 1 ? 365 : day - 1;
  }
  return { minutes, day };
}

export function stepDay(s: AnalysisState, delta: number): Partial<AnalysisState> {
  return { day: ((((s.day - 1 + delta) % 365) + 365) % 365) + 1 };
}

/** Months cycle year → Jan … Dec → year. */
export function stepMonth(s: AnalysisState, delta: number): Partial<AnalysisState> {
  return { month: (((s.month + delta) % 13) + 13) % 13, raster: "irradiance" };
}

/** A raster key pressed again turns the layer off. */
export function toggleRaster(s: AnalysisState, layer: RasterLayer): Partial<AnalysisState> {
  return { raster: s.raster === layer ? "none" : layer };
}
